import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const PROMPTS = [
  {
    label: "Dina förhoppningar",
    heading: "Med hjälp av den här metoden skulle jag vilja...",
    placeholder: "",
    key: "hopes"
  },
  {
    label: "Var du befinner dig",
    heading: "Det jag har mest problem med idag är...",
    placeholder: "",
    key: "struggles"
  },
  {
    label: "Ditt mål",
    heading: "När jag är klar med resan hoppas jag att jag...",
    placeholder: "",
    key: "goals"
  }
];

export default function CheckinPage({ onComplete }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const intention = answers.hopes || "";
      await API.post("/journey/checkin", { answers, intention });
      onComplete();
    } catch {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">

            {/* Step 0 — Warm welcome */}
            {step === 0 && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center pt-8 sm:pt-16"
              >
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-6"
                >
                  Välkommen
                </motion.p>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="heading-font text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-6 leading-snug"
                >
                  Du har tagit det första steget
                </motion.h1>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="text-[#0A0A0A]/60 text-base sm:text-lg max-w-md mx-auto mb-4 leading-relaxed">
                    Det här är början på din inre resa. Det finns inget rätt eller fel — bara du, 
                    dina tankar och ditt eget tempo.
                  </p>
                  <p className="text-[#0A0A0A]/50 max-w-md mx-auto mb-12 leading-relaxed text-sm sm:text-base">
                    Innan du börjar vill jag att du stannar upp en stund. På nästa sida får du 
                    skriva ner var du befinner dig just nu och vad du hoppas på. 
                    Det är bara för dig.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  <Button 
                    onClick={() => setStep(1)}
                    className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base"
                    data-testid="start-checkin-btn"
                  >
                    Jag är redo
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* Steps 1–3 — Sentence starters */}
            {step >= 1 && step <= PROMPTS.length && (
              <motion.div
                key={`prompt-${step}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] text-[#A0655A] font-medium mb-6">
                  {PROMPTS[step - 1].label} · {step} av {PROMPTS.length}
                </p>

                <h2 className="heading-font text-xl sm:text-2xl font-medium text-[#0A0A0A] mb-2 leading-snug">
                  {PROMPTS[step - 1].heading}
                </h2>

                <p className="text-sm text-[#0A0A0A]/30 mb-6">
                  Skriv det som kommer till dig. Det behöver inte vara perfekt.
                </p>

                <Textarea
                  placeholder={PROMPTS[step - 1].placeholder}
                  value={answers[PROMPTS[step - 1].key] || ''}
                  onChange={(e) => setAnswers(prev => ({ 
                    ...prev, 
                    [PROMPTS[step - 1].key]: e.target.value 
                  }))}
                  rows={5}
                  className="rounded-xl mb-6 border-[#EBE3DF] focus:border-[#A0655A] resize-none text-base leading-relaxed"
                  data-testid={`checkin-answer-${step}`}
                />

                <div className="flex justify-between items-center">
                  <Button 
                    onClick={() => setStep(step - 1)} 
                    variant="ghost"
                    className="text-[#A0655A]"
                  >
                    <ChevronLeft size={18} className="mr-1" /> Tillbaka
                  </Button>

                  {step < PROMPTS.length ? (
                    <Button 
                      onClick={() => setStep(step + 1)}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-6"
                    >
                      Nästa
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8"
                      data-testid="complete-checkin-btn"
                    >
                      {saving ? 'Sparar...' : 'Påbörja min resa'}
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  )}
                </div>

                {/* Skip option */}
                <p className="text-center mt-6">
                  <button 
                    onClick={() => step < PROMPTS.length ? setStep(step + 1) : handleSave()}
                    className="text-xs text-[#0A0A0A]/25 hover:text-[#0A0A0A]/40 transition-colors"
                  >
                    Hoppa över
                  </button>
                </p>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </div>
  );
}
